import { FundraisingFund, FundraisingTermSheet, FUND_PRIORITIES, calculateTermSheetMetrics } from '../../types';
import { Card } from "@repo/o37-ui";
import { FileText, Users, Shield, Percent, PieChart, AlertCircle } from "lucide-react";

// Term sheet with fund data
interface TermSheetWithFund extends FundraisingTermSheet {
  fund: FundraisingFund;
}

interface TermSheetKanbanCardProps {
  termSheet: TermSheetWithFund;
  fund: FundraisingFund;
  isDragging: boolean;
  onDragStart: (e: React.DragEvent, termSheet: TermSheetWithFund) => void;
  onDragEnd: () => void;
  onClick: () => void;
  compact?: boolean;
}

export default function TermSheetKanbanCard({ 
  termSheet,
  fund, 
  isDragging, 
  onDragStart, 
  onDragEnd, 
  onClick,
  compact = false 
}: TermSheetKanbanCardProps) {
  const priorityConfig = FUND_PRIORITIES.find(p => p.priority === fund.priority);

  const formatCurrency = (value: number) => {
    if (value >= 1000000000) return `$${(value / 1000000000).toFixed(1)}B`;
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
    return `$${value.toFixed(0)}`;
  };

  const metrics = termSheet.preMoneyValuation 
    ? calculateTermSheetMetrics(termSheet.investmentAmount, termSheet.preMoneyValuation)
    : null;

  // Check expiration
  const expiresAt = termSheet.expiresAt ? new Date(termSheet.expiresAt) : null;
  const isExpiringSoon = expiresAt && expiresAt.getTime() - Date.now() < 7 * 24 * 60 * 60 * 1000;
  const isExpired = expiresAt && expiresAt < new Date();

  // Format anti-dilution
  const formatAntiDilution = (type?: string) => {
    switch(type) {
      case 'broad_based': return 'Broad-based';
      case 'narrow_based': return 'Narrow-based';
      case 'full_ratchet': return 'Full Ratchet';
      case 'none': return 'None';
      default: return type;
    }
  };

  if (compact) {
    return (
      <div
        className={`bg-white rounded-lg border border-neutral-100 p-2 cursor-grab active:cursor-grabbing transition-all duration-100 hover:shadow-sm ${
          isDragging ? 'opacity-40 scale-[0.98]' : ''
        } ${isExpired ? 'opacity-60' : ''}`}
        draggable
        onDragStart={(e) => onDragStart(e, termSheet)}
        onDragEnd={onDragEnd}
        onClick={onClick}
      >
        <div className="flex items-center gap-2">
          {priorityConfig && (
            <div className={`w-1.5 h-1.5 rounded-full ${priorityConfig.color}`} />
          )}
          <div className="text-xs font-medium text-neutral-900 truncate flex-1">{fund.name}</div>
          <span className="text-[10px] font-medium text-emerald-600">
            {formatCurrency(termSheet.investmentAmount)}
          </span>
        </div>
      </div>
    );
  }

  return (
    <Card
      className={`p-3 cursor-grab active:cursor-grabbing transition-all duration-100 hover:shadow-sm group ${
        isDragging ? 'opacity-40 scale-[0.98]' : ''
      } ${isExpired ? 'opacity-60' : ''}`}
      draggable
      onDragStart={(e: React.DragEvent<HTMLDivElement>) => onDragStart(e, termSheet)}
      onDragEnd={onDragEnd}
      onClick={onClick}
    >
      {/* Header */}
      <div className="flex items-start gap-3 mb-3">
        {fund.logoUrl ? (
          <img 
            src={fund.logoUrl} 
            alt={fund.name} 
            className="w-8 h-8 rounded-md object-cover border border-neutral-100"
          />
        ) : (
          <div className="w-8 h-8 rounded-md bg-neutral-50 border border-neutral-100 flex items-center justify-center text-[10px] font-medium text-neutral-500">
            {fund.name.charAt(0)}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <div className="text-sm font-medium text-neutral-900 truncate">{fund.name}</div>
            {priorityConfig && (
              <div 
                className={`w-2 h-2 rounded-full flex-shrink-0 ${priorityConfig.color}`}
                title={priorityConfig.label}
              />
            )}
          </div>
          <div className="flex items-center gap-2 mt-0.5">
             <div className="text-xs font-semibold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded">
               {formatCurrency(termSheet.investmentAmount)}
             </div>
             {metrics && (
               <div className="text-[11px] text-neutral-500">
                 on <span className="font-medium text-neutral-900">{formatCurrency(termSheet.preMoneyValuation!)}</span> valuation
               </div>
             )}
          </div>
        </div>
      </div>

      {/* Key Terms Grid */}
      <div className="grid grid-cols-2 gap-2 mb-3">
        {/* Ownership/Dilution */}
        {metrics && (
          <div className="flex items-center gap-1.5 text-[10px] text-neutral-600 bg-neutral-50 p-1.5 rounded">
            <PieChart className="w-3 h-3 text-neutral-400" />
            <span>{metrics.dilutionPercentage.toFixed(1)}% Dilution</span>
          </div>
        )}
        
        {/* Board Seats */}
        <div className="flex items-center gap-1.5 text-[10px] text-neutral-600 bg-neutral-50 p-1.5 rounded">
          <Users className="w-3 h-3 text-neutral-400" />
          <span>{termSheet.boardSeats} Board Seat{termSheet.boardSeats !== 1 ? 's' : ''}</span>
        </div>

        {/* Liquidation Preference */}
        <div className="flex items-center gap-1.5 text-[10px] text-neutral-600 bg-neutral-50 p-1.5 rounded">
          <Percent className="w-3 h-3 text-neutral-400" />
          <span>{termSheet.liquidationPreference}x Liq Pref</span>
        </div>

        {/* Pro Rata */}
        <div className="flex items-center gap-1.5 text-[10px] text-neutral-600 bg-neutral-50 p-1.5 rounded">
          <Shield className="w-3 h-3 text-neutral-400" />
          <span>{termSheet.proRataRights ? 'Pro-Rata' : 'No Pro-Rata'}</span>
        </div>
      </div>

      {/* Footer Info */}
      <div className="space-y-1.5 pt-2 border-t border-neutral-100">
        {/* Anti-Dilution if special */}
        {termSheet.antiDilution && termSheet.antiDilution !== 'none' && (
           <div className="flex items-center justify-between text-[10px]">
             <span className="text-neutral-500">Anti-Dilution</span>
             <span className="font-medium text-neutral-700">{formatAntiDilution(termSheet.antiDilution)}</span>
           </div>
        )}

        {/* Expiration warning */}
        {expiresAt && (
          <div className={`flex items-center justify-between text-[10px] ${
            isExpired ? 'text-red-600 font-medium' : isExpiringSoon ? 'text-amber-600 font-medium' : 'text-neutral-500'
          }`}>
            <span className="flex items-center gap-1">
               {(isExpired || isExpiringSoon) && <AlertCircle className="w-3 h-3" />}
               Expires
            </span>
            <span>
              {isExpired 
                ? 'Expired' 
                : `${Math.ceil((expiresAt.getTime() - Date.now()) / (1000 * 60 * 60 * 24))}d left`
              }
            </span>
          </div>
        )}

        {/* Document indicator */}
        {termSheet.documentUrl && (
          <div className="flex items-center gap-1.5 text-[10px] text-blue-600 mt-1 hover:underline cursor-pointer" onClick={(e) => {
            e.stopPropagation();
            window.open(termSheet.documentUrl, '_blank');
          }}>
            <FileText className="w-3 h-3" />
            <span>View Term Sheet</span>
          </div>
        )}
      </div>
    </Card>
  );
}
